import React, { useEffect, useMemo, useState } from 'react';
import {
    Form,
    Select,
    Radio,
    Row,
    Col,
    InputNumber,
    DatePicker,
    Tag,
    Divider,
    Button,
    TimePicker,
    Switch,
    Checkbox
} from 'antd';
import { CloseSquareFilled, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { toast } from '../../common/Popover';
import { capitalize, supportsAdvCustomAudienceObjective } from '../../utils/campaignUtils';
import { useMetaSetup } from '../../context/MetaSetupContext';
import { useCampaign } from '../../context/CampaignContext';
import { useAudiences } from '../../hooks/useAudiences';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../../utils';
import { handleApiError } from '../../utils/errorHandler';
import dayjs from 'dayjs';
import { log } from 'console';

const { Option } = Select;

interface AdSetDetailsPanelProps {
    customItems: any[];
    handleCustomSearch: (query: string) => void;
    handleCustomSelect: (item: any) => void;
    customAdSet: any;
    locationOptions: any[];
    searchLocations: (query: string) => void;
    isSearchingLocations: boolean;
    locationSearch: string;
    setLocationSearch: (val: string) => void;
    targetingOptions: any[];
    searchInterests: (query: string) => void;
    isSearchingInterests: boolean;
    targetingSearch: string;
    setTargetingSearch: (val: string) => void;
    fetchDeliveryEstimate: (
        selectLocations: any[],
        excludedLocations: any[],
        selectedTargeting: any[],
        ageRange: [number, number],
        customAudiences?: any[]
    ) => void;
}

export const AdSetDetailsPanel: React.FC<AdSetDetailsPanelProps> = ({
    locationOptions,
    searchLocations,
    isSearchingLocations,
    locationSearch,
    setLocationSearch,
    targetingOptions,
    searchInterests,
    isSearchingInterests,
    targetingSearch,
    setTargetingSearch,
    fetchDeliveryEstimate
}) => {
    const { state, dispatch } = useCampaign();
    const { orgWabaData, loadingWaba } = useMetaSetup();
    const { fetchAudiences, customItems, handleCustomSearch, handleCustomSelect, customAdSet } = useAudiences(state.selectedAdAccountId, state.orgId);
    const [beneficiaryOptions, setBeneficiaryOptions] = useState<{ id: string, name: string }[]>([]);
    const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);

    // Filter WABAs based on selected Org
    const filteredWabas = useMemo(() => {
        const orgData = orgWabaData.find(o => o.orgId === state.orgId);
        return orgData?.wabaDetails || [];
    }, [orgWabaData, state.orgId]);

    // Derived states for mutual exclusivity
    const hasOtherTargeting = useMemo(() => {
        return state.selectLocations.length > 0 ||
            state.excludedLocations.length > 0 ||
            state.selectedTargeting.length > 0 ||
            state.selectedGender !== 'all' ||
            state.ageRange[0] !== 18 ||
            state.ageRange[1] !== 65 ||
            state.advantagePlus;
    }, [state.selectLocations, state.excludedLocations, state.selectedTargeting, state.selectedGender, state.ageRange, state.advantagePlus]);

    const hasCustomAudience = !!state.customAdSetId;
    const canUseAdvCustomAudience = supportsAdvCustomAudienceObjective(state.selectedObjective);

    useEffect(() => {
        if ((!hasCustomAudience || state.advantagePlus || !canUseAdvCustomAudience) && state.advCustomAudience) {
            dispatch({ type: 'SET_FIELD', field: 'advCustomAudience', value: false });
        }
    }, [hasCustomAudience, state.advantagePlus, canUseAdvCustomAudience, state.advCustomAudience, dispatch]);

    useEffect(() => {
        if (!state.setBeneficiary || beneficiaryOptions.length > 0 || beneficiariesLoading) return;

        let cancelled = false;

        const fetchBeneficiaries = async () => {
            setBeneficiariesLoading(true);
            try {
                const response = await coreAPIService.get<any>(CTW_API_ENDPOINT, '/v2/org/verified-beneficiaries');
                // if (cancelled) return;
                // console.log(response,"65789oiulkjhgfdry");


                setBeneficiaryOptions(response?.data || []);
            } catch (error) {
                if (!cancelled) {
                    handleApiError(error, '/beneficiary', 'Failed to load beneficiaries');
                }
            } finally {
                if (!cancelled) {
                    setBeneficiariesLoading(false);
                }
            }
        };

        fetchBeneficiaries();

        return () => {
            cancelled = true;
        };
    }, [state.setBeneficiary, beneficiaryOptions.length, beneficiariesLoading]);

    // --- Handlers ---

    // 


    const handleClearCustomAudience = () => {
        dispatch({
            type: 'SET_MULTIPLE_FIELDS', payload: {
                customAdSetId: null,
                customAdSetName: null,
                useCustomAudiences: false,
                advCustomAudience: false
            }
        });
    };

    const isHierarchicalOverlap = (loc1: any, loc2: any) => {
        if (!loc1 || !loc2) return false;
        // console.log(loc1, loc2);
        // console.log(locationOptions)


        const key1 = String(loc1.key);
        const key2 = String(loc2.key);

        const type1 = loc1.type?.toLowerCase();
        const type2 = loc2.type?.toLowerCase();

        // One is a parent of another via region_id
        if (loc1.region_id && String(loc1.region_id) === key2) return true;
        if (loc2.region_id && String(loc2.region_id) === key1) return true;

        // One is a parent of another via country_code (if parent is a country)
        if (type2 === 'country' && loc1.country_code === key2) return true;
        if (type1 === 'country' && loc2.country_code === key1) return true;

        return false;
    };

    const handleLocationChange = (keys: string[]) => {
        // Find newly added keys
        const existingKeys = state.selectLocations.map((l: any) => String(l.key));
        const addedKeys = keys.filter(k => !existingKeys.includes(String(k)));

        // Find full location objects for all selected keys
        let newLocations = keys.map(key => {
            const existing = state.selectLocations.find(l => String(l.key) === String(key));
            if (existing) return existing;

            const option = locationOptions.find(o => String(o.value) === String(key));
            return option ? {
                key: option.value,
                name: option.label,
                type: option.type,
                country_code: option.country_code,
                country_name: option.country_name,
                region: option.region,
                region_id: option.region_id,
                supports_region: option.supports_region,
                supports_city: option.supports_city,
                geo_hierarchy_name: option.geo_hierarchy_name
            } : null;
        }).filter(Boolean);

        // If we added new locations, filter out any existing ones that overlap with them
        if (addedKeys.length > 0) {
            const addedLocs = newLocations.filter(l => addedKeys.includes(String(l.key)));

            newLocations = newLocations.filter(loc => {
                const isAdded = addedKeys.includes(String(loc.key));
                if (isAdded) return true; // Keep the newly added ones

                // Remove if it overlaps with ANY of the newly added ones
                const hasOverlap = addedLocs.some(addedLoc => isHierarchicalOverlap(loc, addedLoc));
                return !hasOverlap;
            });
        }

        dispatch({ type: 'SET_FIELD', field: 'selectLocations', value: newLocations });
    };

    const handleExcludedLocationChange = (keys: string[]) => {
        // Find newly added keys
        const existingKeys = state.excludedLocations.map((l: any) => String(l.key));
        const addedKeys = keys.filter(k => !existingKeys.includes(String(k)));

        let newLocations = keys.map(key => {
            const existing = state.excludedLocations.find(l => String(l.key) === String(key));
            if (existing) return existing;

            const option = locationOptions.find(o => String(o.value) === String(key));
            return option ? {
                key: option.value,
                name: option.label,
                type: option.type,
                country_code: option.country_code,
                country_name: option.country_name,
                region: option.region,
                region_id: option.region_id
            } : null;
        }).filter(Boolean);

        // If we added new locations, filter out any existing ones that overlap with them
        if (addedKeys.length > 0) {
            const addedLocs = newLocations.filter(l => addedKeys.includes(String(l.key)));

            newLocations = newLocations.filter(loc => {
                const isAdded = addedKeys.includes(String(loc.key));
                if (isAdded) return true; // Keep the newly added ones

                const hasOverlap = addedLocs.some(addedLoc => isHierarchicalOverlap(loc, addedLoc));
                return !hasOverlap;
            });
        }

        dispatch({ type: 'SET_FIELD', field: 'excludedLocations', value: newLocations });
    };

    const hourOptions = Array.from({ length: 25 }, (_, i) => ({
        label: `${i}:00`,
        value: i,
    }));

    const updateSchedule = (start: number, end: number, index: number) => {
        const newSchedule = state.adsetSchedule.map((item, i) =>
            i === index
                ? { ...item, start_minute: start, end_minute: end }
                : item
        );

        dispatch({
            type: 'SET_FIELD',
            field: 'adsetSchedule',
            value: newSchedule,
        });
    };

    const handleTargetingChange = (ids: string[]) => {
        const newTargeting = ids.map(id => {
            const existing = state.selectedTargeting.find(t => t.id === id);
            if (existing) return existing;
            const option = targetingOptions.find(o => o.value === id);
            return option ? {
                id: option.value,
                name: option.label,
                type: option.type,
                country_code: option.country_code,
                country_name: option.country_name,
                region: option.region,
                region_id: option.region_id,
                supports_region: option.supports_region,
                supports_city: option.supports_city,
                geo_hierarchy_name: option.geo_hierarchy_name
            } : null;
        }).filter(Boolean);

        dispatch({ type: 'SET_FIELD', field: 'selectedTargeting', value: newTargeting });
    };



    return (
        <Form layout="vertical" className="mt-4 space-y-6">
            {/* Custom Audience */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Audience</h3>
                <div
                    className={`border rounded-lg px-4 py-2 mb-2
    transition-colors duration-300
    ${state.advantagePlus ? 'border-green-800' : 'border-gray-200'}
  `}
                >
                    <Form.Item label="Advantage +" layout='horizontal' className='m-0'>
                        <Switch className='ml-[90%]'
                            checked={state.advantagePlus}
                            disabled={hasCustomAudience}
                            onChange={(checked) => {
                                dispatch({
                                    type: "SET_MULTIPLE_FIELDS",
                                    payload: {
                                        advantagePlus: checked,
                                        advCustomAudience: checked || !canUseAdvCustomAudience ? false : state.advCustomAudience
                                    }
                                });
                                if (checked) {
                                    handleClearCustomAudience();
                                }
                            }}
                        />
                    </Form.Item>
                </div>
                <Form.Item
                    label="Custom Audience (Optional)"
                    tooltip={state.isEditing ? "The custom audience can not be changed while editing" : ""}
                >
                    <div className="relative">
                        <Select
                            value={state.customAdSetId || null}
                            options={customItems.map(item => ({
                                value: item.id,
                                label: item.name,
                                id: item.id
                            }))}
                            onSearch={handleCustomSearch}
                            placeholder="Search custom audiences"
                            className="w-full"
                            allowClear
                            onSelect={(value, option: any) => {
                                const selected = customItems.find(i => i.id === value);
                                if (!selected) return;
                                handleCustomSelect(selected);
                                dispatch({
                                    type: "SET_MULTIPLE_FIELDS",
                                    payload: {
                                        customAdSetId: selected.id,
                                        customAdSetName: selected.name,
                                        useCustomAudiences: true,
                                        advCustomAudience: state.advantagePlus || !canUseAdvCustomAudience ? false : state.advCustomAudience
                                    }
                                });
                            }}
                            onClear={handleClearCustomAudience}
                            disabled={state.isEditing}
                        />
                    </div>
                </Form.Item>

                {hasCustomAudience && canUseAdvCustomAudience && (
                    <Form.Item className="mb-2">
                        <Checkbox
                            checked={state.advCustomAudience}
                            disabled={state.advantagePlus}
                            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'advCustomAudience', value: e.target.checked })}
                        >
                            Advantage Custom Audience
                        </Checkbox>
                    </Form.Item>
                )}

                <Form.Item className="mb-2">
                    <Checkbox
                        checked={state.setBeneficiary}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            dispatch({
                                type: 'SET_MULTIPLE_FIELDS',
                                payload: {
                                    setBeneficiary: checked,
                                    beneficiary: checked ? state.beneficiary : null
                                }
                            });
                        }}
                    >
                        Set beneficiary (For Securities and Investement Ads)
                    </Checkbox>
                </Form.Item>

                {state.setBeneficiary && (
                    <Form.Item label="Beneficiary">
                        <Select
                            value={state.beneficiary || null}
                            options={beneficiaryOptions.map(beneficiary => ({
                                value: beneficiary.id,
                                label: beneficiary.name
                            }))}
                            onChange={(value) => dispatch({ type: 'SET_FIELD', field: 'beneficiary', value })}
                            onClear={() => dispatch({ type: 'SET_FIELD', field: 'beneficiary', value: null })}
                            placeholder="Select beneficiary"
                            className="w-full"
                            loading={beneficiariesLoading}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                )}

                <Divider className="my-4" />

                {/* Targeting */}
                <div className="space-y-4">
                    <Form.Item
                        label="Locations"
                    // tooltip={hasCustomAudience ? "Clear custom audience to enable location selection." : ""}
                    >
                        <Select
                            mode="multiple"
                            value={state.selectLocations.map((loc: any) => loc.key)}
                            onChange={handleLocationChange}
                            onSearch={(val) => {
                                setLocationSearch(val);
                                searchLocations(val);
                            }}
                            options={locationOptions.map(opt => ({
                                value: opt.value,
                                label: `${opt.label} | ${opt.country_name} (${capitalize(opt.type)})`
                            }))}
                            placeholder="Search locations to include"
                            filterOption={false}
                            showSearch
                            loading={isSearchingLocations}
                            className="w-full"
                        // disabled={hasCustomAudience}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Exclude Locations"
                    // tooltip={hasCustomAudience ? "Clear custom audience to enable excluded location selection." : ""}
                    >
                        <Select
                            mode="multiple"
                            value={state.excludedLocations.map((loc: any) => loc.key)}
                            onChange={handleExcludedLocationChange}
                            onSearch={(val) => {
                                setLocationSearch(val);
                                searchLocations(val);
                            }}
                            options={locationOptions.map(opt => ({
                                value: opt.value,
                                label: `${opt.label} | ${opt.country_name} (${capitalize(opt.type)})`
                            }))}
                            placeholder="Search locations to exclude"
                            filterOption={false}
                            showSearch
                            loading={isSearchingLocations}
                            className="w-full"
                            tagRender={(props) => (
                                <Tag color="error" closable={props.closable} onClose={props.onClose} style={{ marginRight: 3 }}>
                                    {props.label}
                                </Tag>
                            )}
                        // disabled={hasCustomAudience}
                        />
                    </Form.Item>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            label="Gender"
                        // tooltip={hasCustomAudience ? "Clear custom audience to enable gender selection." : ""}
                        >
                            <Radio.Group
                                value={state.selectedGender}
                                onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'selectedGender', value: e.target.value })}
                                className="w-full"
                            // disabled={hasCustomAudience}
                            >
                                <Radio.Button value="all" className="w-1/3 text-center">All</Radio.Button>
                                <Radio.Button value="male" className="w-1/3 text-center">Male</Radio.Button>
                                <Radio.Button value="female" className="w-1/3 text-center">Female</Radio.Button>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            label="Age Range"
                        // tooltip={hasCustomAudience ? "Clear custom audience to enable age range selection." : ""}
                        >
                            <div className="flex items-center space-x-2">
                                <InputNumber
                                    min={18}
                                    max={65}
                                    value={state.ageRange[0]}
                                    onChange={(val) => val && dispatch({ type: 'SET_FIELD', field: 'ageRange', value: [val, state.ageRange[1]] })}
                                    className="w-full"
                                // disabled={hasCustomAudience}
                                />
                                <span className="text-gray-400">to</span>
                                <InputNumber
                                    min={18}
                                    max={65}
                                    value={state.advantagePlus ? 65 : state.ageRange[1]}
                                    onChange={(val) => val && dispatch({ type: 'SET_FIELD', field: 'ageRange', value: [state.ageRange[0], val] })}
                                    className="w-full"
                                    disabled={state.advantagePlus}
                                />
                            </div>
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Interests & Behaviors"
                    // tooltip={hasCustomAudience ? "Clear custom audience to enable interests and behaviors selection." : ""}
                    >
                        <Select
                            mode="multiple"
                            value={state.selectedTargeting.map((t: any) => t.id)}
                            onChange={handleTargetingChange}
                            onSearch={(val) => {
                                setTargetingSearch(val);
                                searchInterests(val);
                            }}
                            options={targetingOptions.map(opt => ({
                                value: opt.value,
                                label: opt.label
                            }))}
                            placeholder="Search interests"
                            filterOption={false}
                            showSearch
                            loading={isSearchingInterests}
                            className="w-full"
                        // disabled={hasCustomAudience}
                        />
                    </Form.Item>
                </div>
            </div>

            {/* Budget & Schedule */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Budget & Schedule</h3>

                <Form.Item label="Budget Type">
                    <Radio.Group
                        value={state.budgetType}
                        onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'budgetType', value: e.target.value })}
                        disabled={state.isEditing}
                    >
                        <Radio value="DAILY">Daily Budget</Radio>
                        <Radio value="LIFETIME">Lifetime Budget</Radio>
                    </Radio.Group>
                </Form.Item>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {state.budgetType === 'DAILY' ? (
                        <Form.Item label="Daily Budget (INR)" required>
                            <InputNumber
                                style={{ width: '100%' }}
                                formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value!.replace(/\₹\s?|(,*)/g, '') as unknown as number}
                                value={state.dailyBudget}
                                onChange={(val) => dispatch({ type: 'SET_FIELD', field: 'dailyBudget', value: val })}
                                min={100}
                                max={10000}
                            />
                        </Form.Item>
                    ) : (
                        <Form.Item label="Lifetime Budget (INR)" required>
                            <InputNumber
                                style={{ width: '100%' }}
                                formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value!.replace(/\₹\s?|(,*)/g, '') as unknown as number}
                                value={state.lifetimeBudget}
                                onChange={(val) => dispatch({ type: 'SET_FIELD', field: 'lifetimeBudget', value: val })}
                                min={100}
                            />
                        </Form.Item>
                    )}
                </div>

                {state.budgetType === 'LIFETIME' && (
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-md font-semibold m-0">Ad Set Scheduling</h3>
                            <Button
                                type="dashed"
                                onClick={() => {
                                    dispatch({ type: 'SET_FIELD', field: 'adsetSchedule', value: [...state.adsetSchedule, { start_minute: 0, end_minute: 1440, days: [1, 2, 3, 4, 5, 6, 0] }] });
                                }}
                                icon={<PlusOutlined />}
                            >
                                Add Schedule Slot
                            </Button>
                        </div>

                        {state?.adsetSchedule?.map((slot, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200 relative">
                                <DeleteOutlined
                                    className='text-red-500 right-4 top-4 absolute cursor-pointer z-10'
                                    onClick={() => {
                                        dispatch({ type: 'SET_FIELD', field: 'adsetSchedule', value: state.adsetSchedule.filter((_, i) => i !== index) });
                                    }}
                                />

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="Time Range">
                                            <Row gutter={8}>
                                                <Col span={11}>
                                                    <Select
                                                        options={hourOptions}
                                                        value={slot.start_minute / 60}
                                                        onChange={(value) => {
                                                            const start = value * 60;
                                                            const end = slot.end_minute;

                                                            if (end && end - start < 60) {
                                                                toast('error', 'Minimum 1 hour difference required');
                                                                return;
                                                            }

                                                            updateSchedule(start, end, index);
                                                        }}
                                                    />
                                                </Col>

                                                <Col span={2} style={{ textAlign: 'center' }}>
                                                    -
                                                </Col>

                                                <Col span={11}>
                                                    <Select
                                                        options={hourOptions}
                                                        value={slot.end_minute / 60}
                                                        onChange={(value) => {
                                                            const start = slot.start_minute;
                                                            const end = value * 60;

                                                            if (end <= start) {
                                                                toast('error', 'End must be after start');
                                                                return;
                                                            }

                                                            if (end - start < 60) {
                                                                toast('error', 'Minimum 1 hour difference required');
                                                                return;
                                                            }

                                                            updateSchedule(start, end, index);
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Days of Week">
                                            <Select
                                                mode="multiple"
                                                placeholder="Select days"
                                                value={slot.days}
                                                onChange={(values) => {
                                                    const newSchedule = [...state.adsetSchedule];
                                                    newSchedule[index] = { ...slot, days: values };
                                                    dispatch({ type: 'SET_FIELD', field: 'adsetSchedule', value: newSchedule });
                                                }}
                                                style={{ width: '100%' }}
                                            >
                                                <Option value={1}>Monday</Option>
                                                <Option value={2}>Tuesday</Option>
                                                <Option value={3}>Wednesday</Option>
                                                <Option value={4}>Thursday</Option>
                                                <Option value={5}>Friday</Option>
                                                <Option value={6}>Saturday</Option>
                                                <Option value={0}>Sunday</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                        label="Start Date"
                        required
                        validateStatus={state.startDate ? 'success' : ''}
                        help={!state.startDate ? 'Please select a start date' : ''}
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            value={state.startDate ? dayjs(state.startDate) : null}
                            onChange={(date) => {
                                console.log(date);

                                dispatch({ type: 'SET_FIELD', field: 'startDate', value: date?.toISOString() || null });
                                // Clear end date if it's before new start date
                                if (state.endDate && date && dayjs(state.endDate).isBefore(date.clone().add(2, 'days'))) {
                                    dispatch({ type: 'SET_FIELD', field: 'endDate', value: null });
                                }
                            }}
                            // onBlur={() => dispatch({ type: 'SET_FIELD', field: 'startDate', value: null })}
                            className="w-full"
                            placeholder="Select start date and time"
                            disabledDate={(current) => {
                                return current && current < dayjs().startOf('day');
                            }}
                            disabledTime={(current) => {
                                if (current && current.isSame('day')) {
                                    const now = dayjs();
                                    return {
                                        disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                                        disabledMinutes: (selectedHour) => {
                                            if (selectedHour === now.hour()) {
                                                return Array.from({ length: now.minute() }, (_, i) => i);
                                            }
                                            return [];
                                        },
                                    };
                                }
                                return {};
                            }}
                            showNow={false}
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        label="End Date"
                        help={
                            !state.endDate && state.startDate
                                ? 'Campaign will run continuously until manually stopped'
                                : ''
                        }
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            value={state.endDate ? dayjs(state.endDate) : null}
                            onChange={(date) => dispatch({ type: 'SET_FIELD', field: 'endDate', value: date?.toISOString() || null })}
                            className="w-full"
                            placeholder={`Select end date ${state.budgetType == "LIFETIME" ? "(required)" : "(optional)"}`}
                            required={state.budgetType == "LIFETIME"}
                            disabled={!state.startDate}
                            disabledDate={(current) => {
                                if (!current || !state.startDate) return false;

                                const minEndDate = dayjs(state.startDate).add(2, 'days').startOf('day');
                                return current < minEndDate;
                            }}
                            disabledTime={(current) => {
                                if (!current || !state.startDate) return {};

                                const startDateTime = dayjs(state.startDate);
                                const minEndDateTime = startDateTime.clone().add(2, 'days');

                                if (current.isSame('day')) {
                                    return {
                                        disabledHours: () => Array.from({ length: minEndDateTime.hour() }, (_, i) => i),
                                        disabledMinutes: (selectedHour) => {
                                            if (selectedHour === minEndDateTime.hour()) {
                                                return Array.from({ length: minEndDateTime.minute() }, (_, i) => i);
                                            }
                                            return [];
                                        },
                                    };
                                }
                                return {};
                            }}
                            showNow={false}
                            allowClear
                        />
                    </Form.Item>
                </div>
            </div>
        </Form>
    );
};
